import { useMemo } from 'react';
import { formatCalls, formatShare, resolveCodeColor } from './nodeChartTheme';

/**
 * Двухсостоянийный булев выход.
 *
 * Столбчатый график здесь дезинформировал: две категории раскрашивались первыми двумя
 * цветами палитры (синий и зелёный), и «Нет» выглядело как ещё одна равноправная тема,
 * а не как отрицательный исход. Крупная доля «Да» плюс одна стековая полоса читаются
 * с расстояния и не требуют легенды.
 */
const NodeBooleanGauge = ({ spec, rows, onSelect }) => {
  const segments = useMemo(() => {
    const total = rows.reduce((sum, row) => sum + row.calls, 0);

    return rows
      .filter((row) => row.calls > 0)
      .map((row, index) => ({
        ...row,
        color: resolveCodeColor(row.code, index, { tone: 'semantic', colorRoles: spec.colorRoles }),
        width: total > 0 ? (row.calls * 100) / total : 0
      }));
  }, [rows, spec.colorRoles]);

  const positive = rows.find((row) => String(row.code) === 'true');

  return (
    <div className="flex h-full w-full flex-col justify-center gap-4 py-2">
      <div>
        <div className="text-3xl font-semibold leading-none">{formatShare(positive?.share ?? 0)}</div>
        <p className="mt-1 text-xs text-muted-foreground">
          {positive?.label || 'Да'} · {formatCalls(positive?.calls ?? 0)}
        </p>
      </div>

      <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted">
        {segments.map((segment) => (
          <button
            key={segment.code}
            type="button"
            data-ev="nodes.gauge.select"
            title={`${segment.label}: ${formatCalls(segment.calls)} (${formatShare(segment.share)})`}
            onClick={() => onSelect?.(segment)}
            disabled={!onSelect}
            className="h-full transition-opacity hover:opacity-80 disabled:cursor-default"
            style={{ width: `${segment.width}%`, backgroundColor: segment.color }}
          />
        ))}
      </div>

      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {segments.map((segment) => (
          <li key={segment.code} className="flex items-center gap-1.5">
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: segment.color }} />
            {segment.label} · {formatCalls(segment.calls)}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default NodeBooleanGauge;
